import { DemoError } from "./demo.js";
import { supabase } from "./supabase.js";
import type { Member } from "./types.js";

/**
 * Who a private tournament is for.
 *
 * An organiser reads and edits the list; a member reads only their own row. Nothing here
 * checks that — `tournament_invites` and the two RPCs refuse anybody else — so a call from
 * anyone but an organiser comes back empty or refused, not half-done. See 20261002000300.
 */
export async function fetchInvitedIds(tournamentId: string): Promise<string[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from("tournament_invites")
    .select("member_id")
    .eq("tournament_id", tournamentId);

  if (error) throw new Error(error.message);
  return (data as { member_id: string }[]).map((row) => row.member_id);
}

export async function inviteToTournament(tournamentId: string, memberId: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("invite_to_tournament", {
    target_tournament: tournamentId,
    target_member: memberId,
  });

  if (error) throw new Error(error.message);
}

export async function uninviteFromTournament(
  tournamentId: string,
  memberId: string,
): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("uninvite_from_tournament", {
    target_tournament: tournamentId,
    target_member: memberId,
  });

  if (error) throw new Error(error.message);
}

/**
 * Who the picker offers: approved members, managed profiles included, who are not on the list
 * already, narrowed by whatever has been typed. A blank search offers nobody — the club is
 * long enough that a list of everyone is not a picker — so the organiser has to ask for a name.
 */
export function invitablePeople(
  members: Member[],
  invitedIds: ReadonlySet<string> | readonly string[],
  search: string,
): Member[] {
  const invited = new Set(invitedIds);
  const needle = search.trim().toLowerCase();
  if (needle.length === 0) return [];

  return members
    .filter((member) => member.status === "active" && !invited.has(member.id))
    .filter(
      (member) =>
        member.displayName.toLowerCase().includes(needle) ||
        (member.handle ?? "").toLowerCase().includes(needle),
    )
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/**
 * Who the register screen offers, given who is invited. A public tournament offers everybody;
 * a private one offers the invited, and anybody already entered, so that somebody who was
 * removed from the list after entering can still see and withdraw their entry. The database
 * makes the same decision in `register_for_event` and is the one that counts — this only
 * keeps the screen from offering a refusal.
 */
export function enterableFor<T extends { id: string }>(
  people: T[],
  tournament: { private: boolean },
  invitedIds: ReadonlySet<string> | readonly string[],
  alreadyIn: (id: string) => boolean = () => false,
): T[] {
  if (!tournament.private) return people;
  const invited = new Set(invitedIds);
  return people.filter((person) => invited.has(person.id) || alreadyIn(person.id));
}
